import { useMemo, useState } from 'react'
import { Bot, KeyRound, X } from 'lucide-react'
import { AI_ROLES } from '../lib/ai'

// 各厂商名下可关联的模型，按特点分类
// 按角色任务特点推荐模型（仅决定目录顶部「本角色推荐」分组，不限制可选范围）
const ROLE_RECOMMENDED_MODELS = {
  architect: { deepseek: ['deepseek-chat'], kimi: ['kimi-k3'], qwen: ['qwen-plus'], openai: ['gpt-5'] },
  reader: { deepseek: ['deepseek-chat'], kimi: ['kimi-k3'], qwen: ['qwen-plus'], openai: ['gpt-5'] },
  critic: { deepseek: ['deepseek-reasoner'], kimi: ['kimi-k3-1-pro'], qwen: ['qwq-plus'], openai: ['o3-mini'] },
  methodologist: { deepseek: ['deepseek-reasoner'], kimi: ['kimi-k3-1-pro'], qwen: ['qwq-plus'], openai: ['o3-mini'] },
  reviewer: { deepseek: ['deepseek-chat'], kimi: ['kimi-k3-1-pro'], qwen: ['qwen-long'], openai: ['gpt-5'] },
}

const AI_MODEL_CATALOG = {
  deepseek: [
    { label: '通用对话', models: [{ id: 'deepseek-chat', name: 'DeepSeek-V3 · 通用对话' }] },
    { label: '深度推理', models: [{ id: 'deepseek-reasoner', name: 'DeepSeek-R1 · 深度推理' }] },
  ],
  kimi: [
    { label: '旗舰 · 深度推理', models: [{ id: 'kimi-k3-1-pro', name: 'Kimi K3.1 Pro · 旗舰' }, { id: 'kimi-k3', name: 'Kimi K3 · 最新旗舰' }] },
    { label: '编程', models: [{ id: 'kimi-k2-7-code', name: 'Kimi K2.7 Code · 编程' }] },
    { label: '多模态', models: [{ id: 'kimi-k2-6', name: 'Kimi K2.6 · 文本/图片/视频' }] },
    { label: '经典（旧）', models: [{ id: 'moonshot-v1-8k', name: 'Kimi 8k · 快速' }, { id: 'moonshot-v1-32k', name: 'Kimi 32k · 长文' }, { id: 'moonshot-v1-128k', name: 'Kimi 128k · 超长文' }] },
  ],
  qwen: [
    { label: '快速便宜', models: [{ id: 'qwen-turbo', name: '通义 Turbo · 快速便宜' }] },
    { label: '均衡', models: [{ id: 'qwen-plus', name: '通义 Plus · 均衡' }] },
    { label: '旗舰', models: [{ id: 'qwen3-max', name: 'Qwen3 Max · 旗舰' }, { id: 'qwen-max', name: '通义 Max · 旗舰' }] },
    { label: '长文本', models: [{ id: 'qwen-long', name: '通义 Long · 长文' }] },
    { label: '深度推理', models: [{ id: 'qwq-plus', name: '通义 QwQ · 深度推理' }] },
  ],
  openai: [
    { label: '快速便宜', models: [{ id: 'gpt-5-mini', name: 'GPT-5 mini · 快速便宜' }] },
    { label: '旗舰', models: [{ id: 'gpt-5', name: 'GPT-5 · 旗舰' }, { id: 'gpt-4.1', name: 'GPT-4.1 · 上一代旗舰' }] },
    { label: '深度推理', models: [{ id: 'o3-mini', name: 'o3-mini · 推理' }, { id: 'o4-mini', name: 'o4-mini · 推理' }] },
  ],
  custom: [],
}

export const AI_PRESETS = {
  local: { label: '本地引擎（默认）', baseUrl: '', model: '' },
  deepseek: { label: 'DeepSeek', baseUrl: 'https://api.deepseek.com/v1', model: 'deepseek-chat' },
  kimi: { label: 'Kimi（月之暗面）', baseUrl: 'https://api.moonshot.cn/v1', model: 'kimi-k3' },
  qwen: { label: '通义千问', baseUrl: 'https://dashscope.aliyuncs.com/compatible-mode/v1', model: 'qwen-plus' },
  openai: { label: 'OpenAI', baseUrl: 'https://api.openai.com/v1', model: 'gpt-5-mini' },
  custom: { label: '自定义（OpenAI 兼容接口）', baseUrl: '', model: '' },
}

export function normalizeSettings(payload) {
  const roles = {}
  const keys = { ...((payload && payload.keys) || {}) }
  for (const role of AI_ROLES) {
    const saved = payload && payload.roles && payload.roles[role.id]
    const provider = (saved && saved.provider) || 'local'
    // 旧版本把 Key 存在角色里：迁移到厂商级共享 Key
    if (provider !== 'local' && saved && saved.apiKey && !keys[provider]) {
      keys[provider] = saved.apiKey
    }
    roles[role.id] = {
      provider,
      baseUrl: (saved && saved.baseUrl) || '',
      model: (saved && saved.model) || '',
    }
  }
  return { roles, keys }
}

export default function AISettingsModal({ settings, onSave, onClose, notify }) {
  const normalized = normalizeSettings(settings)
  const [draft, setDraft] = useState(() => normalized.roles)
  const [keys, setKeys] = useState(() => normalized.keys)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  function patch(roleId, patchData) {
    setDraft((current) => ({ ...current, [roleId]: { ...current[roleId], ...patchData } }))
  }

  function patchKey(provider, value) {
    setKeys((current) => ({ ...current, [provider]: value }))
  }

  function deleteKey(provider) {
    setKeys((current) => {
      const next = { ...current }
      delete next[provider]
      return next
    })
  }

  function changeProvider(roleId, provider) {
    const preset = AI_PRESETS[provider] || AI_PRESETS.custom
    patch(roleId, { provider, baseUrl: preset.baseUrl, model: preset.model })
  }

  const cloudCount = useMemo(() => Object.values(draft).filter((item) => item.provider !== 'local').length, [draft])

  const cloudProviders = useMemo(() => [...new Set(Object.values(draft).filter((item) => item.provider !== 'local').map((item) => item.provider))], [draft])

  async function submit() {
    if (busy) return
    for (const item of Object.values(draft).filter((entry) => entry.provider !== 'local')) {
      if (!item.baseUrl.trim()) { setError('云端角色需要填写接口地址（Base URL）。'); return }
      if (!item.model.trim()) { setError('云端角色需要填写模型名称。'); return }
    }
    for (const provider of cloudProviders) {
      if (!(keys[provider] || '').trim()) { setError('请为厂商「' + (AI_PRESETS[provider] ? AI_PRESETS[provider].label : provider) + '」填写 API Key。'); return }
    }
    setBusy(true)
    setError('')
    try {
      await onSave({ roles: draft, keys })
    } catch (err) {
      setError(err.message || '保存失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="history-modal ai-settings-modal" onClick={(event) => event.stopPropagation()}>
        <header>
          <div>
            <h3>AI 设置</h3>
            <p>每个圆桌成员可以单独选择厂商、模型和 Key；配置保存在你的云端账号里，换设备自动同步。</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭"><X size={18} /></button>
        </header>
        <div className="ai-settings-list">
          {AI_ROLES.map((role) => {
            const item = draft[role.id] || { provider: 'local', baseUrl: '', model: '', apiKey: '' }
            return (
              <div className="ai-role-row" key={role.id}>
                <div className="ai-role-row__head">
                  <span className="ai-mini" style={{ background: role.color }} title={role.name + ' · ' + role.title}>{role.initials}</span>
                  <div>
                    <strong>{role.name}</strong>
                    <small>{role.title}</small>
                  </div>
                  <select value={item.provider} onChange={(event) => changeProvider(role.id, event.target.value)}>
                    {Object.entries(AI_PRESETS).map(([id, preset]) => (
                      <option key={id} value={id}>{preset.label}</option>
                    ))}
                  </select>
                </div>
                {item.provider !== 'local' ? (
                  <div className="ai-role-row__fields">
                    <label>
                      <span>接口地址 Base URL</span>
                      <input value={item.baseUrl} placeholder="https://api.example.com/v1" onChange={(event) => patch(role.id, { baseUrl: event.target.value })} />
                    </label>
                    <label>
                      <span>模型 · 按特点分类</span>
                      {(function () {
                        const catalog = AI_MODEL_CATALOG[item.provider] || []
                        const catalogIds = catalog.flatMap((cat) => cat.models.map((model) => model.id))
                        const isCatalogModel = catalogIds.includes(item.model)
                        return (
                          <>
                            <select
                              value={isCatalogModel ? item.model : 'custom'}
                              onChange={(event) => {
                                if (event.target.value === 'custom') {
                                  patch(role.id, { model: '' })
                                } else {
                                  patch(role.id, { model: event.target.value })
                                }
                              }}
                              aria-label={'模型分类'}
                            >
                              <option value="custom">自定义模型名…</option>
                              {(function () {
                                const recs = (ROLE_RECOMMENDED_MODELS[role.id] || {})[item.provider] || []
                                const recModels = []
                                const recIds = new Set()
                                for (const cat of catalog) {
                                  for (const model of cat.models) {
                                    if (recs.includes(model.id)) { recModels.push(model); recIds.add(model.id) }
                                  }
                                }
                                return (
                                  <>
                                    {recModels.length ? (
                                      <optgroup label="本角色推荐">
                                        {recModels.map((model) => <option key={model.id} value={model.id}>{model.name}</option>)}
                                      </optgroup>
                                    ) : null}
                                    {catalog.map((cat) => (
                                      <optgroup key={cat.label} label={cat.label}>
                                        {cat.models.filter((model) => !recIds.has(model.id)).map((model) => <option key={model.id} value={model.id}>{model.name}</option>)}
                                      </optgroup>
                                    ))}
                                  </>
                                )
                              })()}
                            </select>
                            {!isCatalogModel ? (
                              <input value={item.model} placeholder="输入模型名，如 deepseek-chat" onChange={(event) => patch(role.id, { model: event.target.value })} />
                            ) : null}
                          </>
                        )
                      })()}
                    </label>
                    <span className="ai-key-hint">
                      <KeyRound size={11} /> API Key：{keys[item.provider] ? '已关联（该厂商共享）' : '未填写'}
                    </span>
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
        {cloudProviders.length ? (
          <div className="ai-provider-keys">
            <div className="ai-provider-keys__head">
              <KeyRound size={14} />
              <strong>API Key（按厂商共享）</strong>
              <em>填一次即可，所有使用该厂商的角色自动关联；删除后各角色会回退本地引擎。</em>
            </div>
            {cloudProviders.map((provider) => (
              <div className="ai-provider-key-row" key={provider}>
                <span>{(AI_PRESETS[provider] && AI_PRESETS[provider].label) || provider}</span>
                <input
                  type="password"
                  value={keys[provider] || ''}
                  placeholder="sk-..."
                  autoComplete="off"
                  aria-label={'API Key ' + provider}
                  onChange={(event) => patchKey(provider, event.target.value)}
                />
                {keys[provider] ? (
                  <button className="danger-link" onClick={() => deleteKey(provider)} aria-label={'删除 ' + provider + ' 的 API Key'}>
                    <X size={13} /> 删除
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        <div className="ai-settings-foot">
          <span className="ai-settings-count">{cloudCount} 个角色使用云端模型 · 其余用本地引擎</span>
          {error ? <span className="auth-error">{error}</span> : null}
          <div>
            <button className="btn btn--ghost" onClick={onClose}>取消</button>
            <button className="btn btn--ink" onClick={submit} disabled={busy}>
              <Bot size={14} /> {busy ? '保存中…' : '保存并同步'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
